/**
 * Shared route-level screens.
 *
 * They live together because they share one shape: an illustration-free, centred message with exactly one action.
 * Keeping them in one module makes it obvious that no screen may offer a dead end — every terminal state has a way
 * back into the application.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card } from '@/components/ui/surface';
import { Button } from '@/components/ui/button';
import { POST_AUTH_ROUTE, useIsAuthenticated } from '@/features/auth/hooks/useAuth';

/** Props accepted by {@link MessageScreen}. */
interface MessageScreenProps {
  /** Heading of the screen. */
  title: string;
  /** Supporting sentence. */
  body: string;
  /** Primary action. */
  action: { label: string; to: string };
  /** Optional secondary action. */
  secondaryAction?: { label: string; to: string };
}

/** Centred message with one or two actions. */
function MessageScreen({ title, body, action, secondaryAction }: MessageScreenProps) {
  return (
    <Card className="mx-auto mt-10 flex max-w-lg flex-col items-center gap-3 p-8 text-center sm:mt-20">
      <h2 className="text-xl font-bold">{title}</h2>
      <p className="text-sm text-muted-foreground">{body}</p>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
        <Button asChild>
          <Link to={action.to}>{action.label}</Link>
        </Button>

        {secondaryAction ? (
          <Button asChild variant="outline">
            <Link to={secondaryAction.to}>{secondaryAction.label}</Link>
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

/** Unknown address. */
export function NotFoundPage() {
  const { t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();

  return (
    <MessageScreen
      title={t('notFound.title')}
      body={t('notFound.body')}
      action={{
        // A signed-in visitor belongs in the application; a visitor without a session belongs on the landing page,
        // because sending them to a protected address would only bounce them through the sign-in screen.
        to: isAuthenticated ? POST_AUTH_ROUTE : '/',
        label: isAuthenticated ? t('notFound.action') : t('common.appName'),
      }}
    />
  );
}

/** Authenticated but not permitted. */
export function ForbiddenPage() {
  const { t } = useTranslation();

  return (
    <MessageScreen
      title={t('forbidden.title')}
      body={t('forbidden.body')}
      action={{ to: POST_AUTH_ROUTE, label: t('forbidden.action') }}
    />
  );
}

/** Props accepted by {@link ComingSoonPage}. */
export interface ComingSoonPageProps {
  /** Translation key of the section title. */
  titleKey: string;
}

/**
 * Placeholder for a route that exists in the navigation but is delivered in a later phase.
 *
 * It is a deliberate product decision rather than a shortcut: hiding the entries would leave users unable to see the
 * shape of the product, while a broken link would look like a defect. The route is real, the destination is labelled.
 */
export function ComingSoonPage({ titleKey }: ComingSoonPageProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold tracking-tight">{t(titleKey)}</h1>

      <Card className="flex flex-col gap-2 p-6">
        <p className="text-sm text-muted-foreground">{t('common.comingSoon')}</p>
        <Link className="text-sm text-primary hover:underline" to={POST_AUTH_ROUTE}>
          {t('dashboard.quickStartTitle')}
        </Link>
      </Card>
    </div>
  );
}
